import React, { useState, useEffect, useCallback } from 'react';
import {
  utilityMetersApi,
  UtilityMeter,
  UtilityMeterActionLog,
  CreateUtilityMeterData,
} from '../../api/utilityMetersApi';
import ImportFileLink from '../common/ImportFileLink';
import { MeterReadingType } from '../../api/meterReadingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { UtilityMeterEditModal } from './UtilityMeterEditModal';
import { UtilityMeterImportModal } from './UtilityMeterImportModal';
import { UtilityMeterQrPrintModal, MeterQrPrintItem } from './UtilityMeterQrPrintModal';
import { BulkListPagination } from './BulkListPagination';
import {
  getQuotaTierLabel,
  getBillingModeLabel,
  getMeterStatusLabel,
  buildQuotaTierOptionsFromFeeTiers,
} from '../../utils/quotaTierLabels';
import { feeCategoriesApi } from '../../api/feeCategoriesApi';
import { findUsageFeeCategoryForMeterType } from '../../utils/usageFeeCategoryLookup';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import { BUILDING_UTILITY_PERMISSIONS } from '../../constants/functionPermissions';

interface UtilityMetersPanelProps {
  buildingId: string;
  buildingName: string;
  type: MeterReadingType;
  t: (key: string) => string;
}

const PAGE_SIZE = 25;
const HISTORY_PAGE_SIZE = 25;

type PanelView = 'list' | 'history';

export const UtilityMetersPanel: React.FC<UtilityMetersPanelProps> = ({
  buildingId,
  buildingName,
  type,
  t,
}) => {
  const { can } = useFunctionPermissions();
  const canImportMeters = can('UTILITY_METER_IMPORT', 'import');
  const canCreateMeter = can(BUILDING_UTILITY_PERMISSIONS.create, 'create');
  const canEditMeter = can(BUILDING_UTILITY_PERMISSIONS.edit, 'edit');
  const canDeleteMeter = can(BUILDING_UTILITY_PERMISSIONS.delete, 'delete');
  const [meters, setMeters] = useState<UtilityMeter[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMeter, setEditingMeter] = useState<UtilityMeter | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedMeterIds, setSelectedMeterIds] = useState<Set<string>>(new Set());
  const [selectedMetersMap, setSelectedMetersMap] = useState<Map<string, UtilityMeter>>(new Map());
  const [showQrPrint, setShowQrPrint] = useState(false);
  const [qrPrintMeters, setQrPrintMeters] = useState<MeterQrPrintItem[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [newMeter, setNewMeter] = useState<Partial<CreateUtilityMeterData>>({
    billingMode: 'NON_QUOTA',
    quotaTierLevel: 1,
    initialReading: 0,
    status: 'ACTIVE',
  });
  const [quotaTierOptions, setQuotaTierOptions] = useState<number[]>([1, 2, 3, 4]);
  const [panelView, setPanelView] = useState<PanelView>('list');
  const [actionLogs, setActionLogs] = useState<UtilityMeterActionLog[]>([]);
  const [historySearch, setHistorySearch] = useState('');
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedHistorySearch(historySearch), 300);
    return () => clearTimeout(timer);
  }, [historySearch]);

  const loadMeters = useCallback(async () => {
    if (!buildingId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await utilityMetersApi.list({
        buildingId,
        type,
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      });
      setMeters(result.data);
      setTotalPages(result.meta.totalPages);
      setTotalRecords(result.meta.total);
    } catch {
      setError(t('failedToLoadMeters'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, type, debouncedSearch, currentPage, t]);

  useEffect(() => {
    if (panelView === 'list') {
      loadMeters();
    }
  }, [loadMeters, panelView]);

  const loadActionLogs = useCallback(async () => {
    if (!buildingId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await utilityMetersApi.getActionLogs({
        buildingId,
        type,
        page: historyPage,
        limit: HISTORY_PAGE_SIZE,
        search: debouncedHistorySearch || undefined,
      });
      setActionLogs(result.data);
      setHistoryTotal(result.meta.total);
      setHistoryTotalPages(result.meta.totalPages);
    } catch {
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, type, historyPage, debouncedHistorySearch, t]);

  useEffect(() => {
    if (panelView === 'history' && buildingId) {
      loadActionLogs();
    }
  }, [panelView, buildingId, loadActionLogs]);

  useEffect(() => {
    setPanelView('list');
    setHistoryPage(1);
  }, [buildingId, type]);

  useEffect(() => {
    if (!buildingId) {
      setQuotaTierOptions([1, 2, 3, 4]);
      return;
    }
    feeCategoriesApi
      .getAll(buildingId)
      .then((categories) => {
        const usageFee = findUsageFeeCategoryForMeterType(categories, type);
        setQuotaTierOptions(buildQuotaTierOptionsFromFeeTiers(usageFee?.feeTiers));
      })
      .catch(() => setQuotaTierOptions([1, 2, 3, 4]));
  }, [buildingId, type]);

  useEffect(() => {
    if (!buildingId) return;
    apartmentsApi
      .getApartments({ buildingId })
      .then((data) => setApartments(data))
      .catch(() => setApartments([]));
  }, [buildingId]);

  useEffect(() => {
    setSelectedMeterIds(new Set());
    setSelectedMetersMap(new Map());
    setCurrentPage(1);
  }, [buildingId, type]);

  const fetchAllMeters = async (): Promise<UtilityMeter[]> => {
    return utilityMetersApi.listAll({
      buildingId,
      type,
      search: debouncedSearch || undefined,
    });
  };

  const handleExport = async () => {
    try {
      await utilityMetersApi.exportMeters(buildingId, type);
    } catch {
      setError(t('failedToExportSample'));
    }
  };

  const toQrPrintItem = (meter: UtilityMeter): MeterQrPrintItem => ({
    meterCode: meter.meterCode,
    qrValue: (meter.qrCode?.trim() || meter.meterCode).trim(),
  });

  const openQrPrint = (targetMeters: UtilityMeter[]) => {
    if (targetMeters.length === 0) {
      setError(t('noMetersSelectedForQr'));
      return;
    }
    setError(null);
    setQrPrintMeters(targetMeters.map(toQrPrintItem));
    setShowQrPrint(true);
  };

  const handleExportQrSelected = () => {
    openQrPrint(Array.from(selectedMetersMap.values()));
  };

  const handleExportQrAll = async () => {
    try {
      setLoading(true);
      const allMeters = await fetchAllMeters();
      openQrPrint(allMeters);
    } catch {
      setError(t('failedToLoadMeters'));
    } finally {
      setLoading(false);
    }
  };

  const toggleMeterSelection = (meter: UtilityMeter, checked: boolean) => {
    setSelectedMeterIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(meter.id);
      } else {
        next.delete(meter.id);
      }
      return next;
    });
    setSelectedMetersMap((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(meter.id, meter);
      } else {
        next.delete(meter.id);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectedMeterIds((prev) => {
      const next = new Set(prev);
      meters.forEach((m) => (checked ? next.add(m.id) : next.delete(m.id)));
      return next;
    });
    setSelectedMetersMap((prev) => {
      const next = new Map(prev);
      meters.forEach((m) => (checked ? next.set(m.id, m) : next.delete(m.id)));
      return next;
    });
  };

  const allOnPageSelected =
    meters.length > 0 && meters.every((m) => selectedMeterIds.has(m.id));
  const someOnPageSelected = meters.some((m) => selectedMeterIds.has(m.id));

  const handleCreateMeter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMeter.apartmentId || !newMeter.meterCode?.trim()) {
      setError(t('meterCreateRequiredFields'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await utilityMetersApi.create({
        buildingId,
        type,
        apartmentId: newMeter.apartmentId,
        meterCode: newMeter.meterCode.trim(),
        qrCode: newMeter.qrCode,
        billingMode: newMeter.billingMode,
        feeMultiplier: newMeter.feeMultiplier,
        quotaTierLevel: newMeter.quotaTierLevel ?? 1,
        registrationDate: newMeter.registrationDate,
        initialReading: newMeter.initialReading ?? 0,
        status: newMeter.status,
        notes: newMeter.notes,
      });
      setShowAddForm(false);
      setNewMeter({
        billingMode: 'NON_QUOTA',
        quotaTierLevel: 1,
        initialReading: 0,
        status: 'ACTIVE',
      });
      loadMeters();
    } catch (err: any) {
      setError(err.response?.data?.message || t('failedToCreateMeter'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (meter: UtilityMeter) => {
    if (!window.confirm(t('confirmDeleteMeter').replace('{code}', meter.meterCode))) return;
    try {
      await utilityMetersApi.remove(meter.id);
      if (panelView === 'list') {
        loadMeters();
      } else {
        loadActionLogs();
      }
    } catch {
      setError(t('failedToDeleteMeter'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMeterIds.size === 0) return;
    const count = selectedMeterIds.size;
    if (!window.confirm(t('confirmBulkDeleteMeters').replace('{count}', String(count)))) return;
    try {
      setLoading(true);
      const result = await utilityMetersApi.bulkDelete({
        buildingId,
        type,
        meterIds: Array.from(selectedMeterIds),
      });
      alert(
        t('bulkDeleteMetersResultSummary')
          .replace('{deleted}', String(result.deletedCount))
          .replace('{skipped}', String(result.skippedCount)),
      );
      setSelectedMeterIds(new Set());
      setSelectedMetersMap(new Map());
      if (panelView === 'list') {
        loadMeters();
      } else {
        loadActionLogs();
      }
    } catch {
      setError(t('failedToDeleteMeter'));
    } finally {
      setLoading(false);
    }
  };

  const formatUtilityMeterAction = (log: UtilityMeterActionLog) => {
    switch (log.action) {
      case 'IMPORT':
        return t('cardActionImport');
      case 'DELETE':
        return t('cardActionDelete');
      case 'BULK_DELETE':
        return t('activityHistoryBulkDelete');
      default:
        return log.action;
    }
  };

  const getUtilityMeterActionBadgeClass = (log: UtilityMeterActionLog) => {
    switch (log.action) {
      case 'IMPORT':
        return 'bg-purple-100 text-purple-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'BULK_DELETE':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatUtilityMeterActionNotes = (log: UtilityMeterActionLog) => {
    if (log.notes) return log.notes;
    const metadata = log.metadata ?? {};
    if (log.action === 'BULK_DELETE' && Array.isArray(metadata.deletedMeters)) {
      const codes = (metadata.deletedMeters as Array<{ meterCode?: string }>)
        .map((row) => row.meterCode)
        .filter(Boolean)
        .slice(0, 8);
      const suffix =
        (metadata.deletedCount as number) > codes.length
          ? ` … (+${(metadata.deletedCount as number) - codes.length})`
          : '';
      return codes.length > 0 ? `${codes.join(', ')}${suffix}` : '--';
    }
    return '--';
  };

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setPanelView('list')}
          className={`px-6 py-3 font-medium border-b-2 -mb-px ${
            panelView === 'list'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('meterList')}
        </button>
        <button
          type="button"
          onClick={() => {
            setPanelView('history');
            setHistoryPage(1);
          }}
          className={`px-6 py-3 font-medium border-b-2 -mb-px ${
            panelView === 'history'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('meterReadingHistoryTab')}
        </button>
      </div>

      {panelView === 'list' && (
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          type="button"
          onClick={handleExportQrSelected}
          disabled={!buildingId || loading || selectedMeterIds.size === 0}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
        >
          📱 {t('exportMeterQrSelected')}
        </button>
        <button
          type="button"
          onClick={handleExportQrAll}
          disabled={!buildingId || loading || totalRecords === 0}
          className="bg-green-100 hover:bg-green-200 text-green-800 px-4 py-2 rounded-lg disabled:opacity-50"
        >
          📱 {t('exportMeterQrAll')}
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={!buildingId || loading}
          className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
        >
          📋 {t('exportMeters')}
        </button>
        {canImportMeters && (
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-4 py-2 rounded-lg"
          >
            📥 {t('importMeters')}
          </button>
        )}
        {canCreateMeter && (
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            ➕ {t('addMeter')}
          </button>
        )}
        {canDeleteMeter && selectedMeterIds.size > 0 && (
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={loading}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            🗑️ {t('deleteSelectedMeters')} ({selectedMeterIds.size})
          </button>
        )}
      </div>
      )}

      {panelView === 'list' && showAddForm && canCreateMeter && (
        <form
          onSubmit={handleCreateMeter}
          className="bg-white border border-gray-200 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">{t('apartmentCode')}</label>
            <select
              required
              value={newMeter.apartmentId ?? ''}
              onChange={(e) => setNewMeter({ ...newMeter, apartmentId: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">{t('selectApartment')}</option>
              {apartments.map((apt) => (
                <option key={apt.id} value={apt.id}>
                  {apt.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('meterCode')}</label>
            <input
              required
              value={newMeter.meterCode ?? ''}
              onChange={(e) => setNewMeter({ ...newMeter, meterCode: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">QR</label>
            <input
              value={newMeter.qrCode ?? ''}
              onChange={(e) => setNewMeter({ ...newMeter, qrCode: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('billingMode')}</label>
            <select
              value={newMeter.billingMode ?? 'NON_QUOTA'}
              onChange={(e) =>
                setNewMeter({
                  ...newMeter,
                  billingMode: e.target.value as 'QUOTA' | 'NON_QUOTA',
                })
              }
              className="w-full border rounded px-3 py-2"
            >
              <option value="QUOTA">{t('billingModeQuota')}</option>
              <option value="NON_QUOTA">{t('billingModeNonQuota')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('feeMultiplier')}</label>
            <input
              type="number"
              min={0}
              value={newMeter.feeMultiplier ?? ''}
              onChange={(e) =>
                setNewMeter({
                  ...newMeter,
                  feeMultiplier: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                })
              }
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('quotaTierStart')}</label>
            <select
              value={String(newMeter.quotaTierLevel ?? 1)}
              onChange={(e) =>
                setNewMeter({
                  ...newMeter,
                  quotaTierLevel: parseInt(e.target.value, 10) || 1,
                })
              }
              className="w-full border rounded px-3 py-2"
            >
              {quotaTierOptions.map((level) => (
                <option key={level} value={level}>
                  {getQuotaTierLabel(level, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 border rounded">
              {t('cancel')}
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
              {t('save')}
            </button>
          </div>
        </form>
      )}

      {panelView === 'history' && (
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 border-b flex flex-wrap justify-between items-center gap-4">
            <input
              type="text"
              value={historySearch}
              onChange={(e) => {
                setHistorySearch(e.target.value);
                setHistoryPage(1);
              }}
              placeholder={t('searchApartmentOrMeter')}
              className="border rounded-lg px-3 py-2 text-sm min-w-[220px]"
            />
            <div className="text-sm text-gray-600">
              {t('totalRecords')}: {historyTotal}
            </div>
          </div>
          <div className="overflow-x-auto p-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">{t('loading')}</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('exportTime')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('actions')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('apartmentCode')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('meterCode')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('notes')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('importHistoryFileName')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('cardActionStaff')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {actionLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleString('vi-VN')}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getUtilityMeterActionBadgeClass(log)}`}
                          >
                            {formatUtilityMeterAction(log)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">
                          {log.apartmentCode ?? '--'}
                        </td>
                        <td className="px-4 py-3 text-sm">{log.meterCode ?? '--'}</td>
                        <td className="px-4 py-3 text-sm max-w-md whitespace-pre-wrap break-words">
                          {formatUtilityMeterActionNotes(log)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <ImportFileLink fileName={log.fileName} fileUrl={log.fileUrl} />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {log.creator?.name || log.creator?.email || '--'}
                        </td>
                      </tr>
                    ))}
                    {actionLogs.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          {t('noData')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <BulkListPagination
                  page={historyPage}
                  totalPages={historyTotalPages}
                  total={historyTotal}
                  onPageChange={setHistoryPage}
                  t={t}
                />
              </>
            )}
          </div>
        </div>
      )}

      {panelView === 'list' && (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b flex justify-between items-center gap-4">
          <h3 className="font-medium">{t('meterList')}</h3>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder={t('searchApartmentOrMeter')}
            className="border rounded-lg px-3 py-2"
          />
        </div>
        <div className="overflow-x-auto p-4">
          {loading && meters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('loading')}</div>
          ) : totalRecords === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('noMeters')}</div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = someOnPageSelected && !allOnPageSelected;
                          }
                        }}
                        onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                        aria-label={t('selectAllFiltered')}
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apartmentCode')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('meterCode')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('billingMode')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('feeMultiplier')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('quotaTierStart')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('initialReading')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('status')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {meters.map((meter, index) => (
                    <tr key={meter.id}>
                      <td className="px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedMeterIds.has(meter.id)}
                          onChange={(e) => toggleMeterSelection(meter, e.target.checked)}
                          aria-label={meter.meterCode}
                        />
                      </td>
                      <td className="px-3 py-2 text-sm">{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium text-blue-600">{meter.apartmentCode}</td>
                      <td className="px-3 py-2 text-sm">{meter.meterCode}</td>
                      <td className="px-3 py-2 text-sm">{meter.qrCode ?? '-'}</td>
                      <td className="px-3 py-2 text-sm">{getBillingModeLabel(meter.billingMode, t)}</td>
                      <td className="px-3 py-2 text-sm">{meter.feeMultiplier ?? '-'}</td>
                      <td className="px-3 py-2 text-sm">
                        {getQuotaTierLabel(meter.quotaTierLevel ?? meter.quotaTierStart, t)}
                      </td>
                      <td className="px-3 py-2 text-sm">{meter.initialReading}</td>
                      <td className="px-3 py-2 text-sm">{getMeterStatusLabel(meter.status, t)}</td>
                      <td className="px-3 py-2 text-sm space-x-2">
                        {canEditMeter && (
                          <button type="button" onClick={() => setEditingMeter(meter)} className="text-blue-600">
                            ✏️
                          </button>
                        )}
                        {canDeleteMeter && (
                          <button type="button" onClick={() => handleDelete(meter)} className="text-red-600">
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <BulkListPagination
                page={currentPage}
                totalPages={totalPages}
                total={totalRecords}
                onPageChange={setCurrentPage}
                t={t}
              />
            </>
          )}
        </div>
      </div>
      )}

      <UtilityMeterQrPrintModal
        isOpen={showQrPrint}
        meters={qrPrintMeters}
        onClose={() => setShowQrPrint(false)}
        t={t}
      />

      <UtilityMeterImportModal
        isOpen={showImportModal}
        buildingId={buildingId}
        buildingName={buildingName}
        type={type}
        t={t}
        onClose={() => setShowImportModal(false)}
        onImported={(result) => {
          alert(
            t('importMetersResult')
              .replace('{created}', String(result.created))
              .replace('{updated}', String(result.updated))
              .replace('{skipped}', String(result.skipped)),
          );
          loadMeters();
          if (panelView === 'history') {
            loadActionLogs();
          }
        }}
      />

      <UtilityMeterEditModal
        meter={editingMeter}
        isOpen={!!editingMeter}
        onClose={() => setEditingMeter(null)}
        onSaved={() => loadMeters()}
        t={t}
        quotaTierOptions={quotaTierOptions}
      />
    </div>
  );
};
